import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import D3EAppTemplateCategory from "../models/D3EAppTemplateCategory";
import TitleWithCheckbox from "./TitleWithCheckbox";

type _D3EAppTemplateSelectionViewOnAddTemplateCategory = (
  category: D3EAppTemplateCategory
) => void;

type _D3EAppTemplateSelectionViewOnRemoveTemplateCategory = (
  category: D3EAppTemplateCategory
) => void;

type _SelectedCategoryOnChanged = (
  value: boolean,
  d3eState: D3EAppTemplateSelectionViewRefs
) => void;

export interface D3EAppTemplateSelectionViewProps extends BaseUIProps {
  key?: string;
  template?: D3EAppTemplateCategory;
  onAddTemplateCategory?: _D3EAppTemplateSelectionViewOnAddTemplateCategory;
  onRemoveTemplateCategory?: _D3EAppTemplateSelectionViewOnRemoveTemplateCategory;
}
/// To store state data for D3EAppTemplateSelectionView
class D3EAppTemplateSelectionViewRefs {
  public rowRef: RowRefState = new RowRefState();
}

interface RowRefWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: D3EAppTemplateSelectionViewRefs;
  _onSelectedCategory?: _SelectedCategoryOnChanged;
  isSelected: boolean;
  template: D3EAppTemplateCategory;
}

class RowRefState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _RowRefWithState extends ObservableComponent<RowRefWithStateProps> {
  public constructor(props: RowRefWithStateProps) {
    super(props);

    this.initState();
  }
  public get isSelected(): boolean {
    return this.props.isSelected;
  }
  public get rowRef(): RowRefState {
    return this.props.d3eState.rowRef;
  }
  public get template(): D3EAppTemplateCategory {
    return this.props.template;
  }
  public get d3eState(): D3EAppTemplateSelectionViewRefs {
    return this.props.d3eState;
  }
  public get _onSelectedCategory(): _SelectedCategoryOnChanged {
    return this.props._onSelectedCategory;
  }
  public initState() {
    super.initState();

    this.updateObservable("rowRef", null, this.rowRef);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("template", this.props.template);

    this.on(
      ["isSelected", "rowRef", "rowRef.hover", "template", "template.title"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: RowRefWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.template !== this.props.template) {
      this.updateObservable(
        "template",
        prevProps.template,
        this.props.template
      );

      this.fire("template", this);
    }
  }
  public rowRefOnEnter(event): void {
    return this.rowRef.setHover(true);
  }
  public rowRefOnExit(event): void {
    return this.rowRef.setHover(false);
  }
  public dispose(): void {
    this.rowRef.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    return ui.Container({
      states: ui.joinStates(
        { "data-c0": this.rowRef.hover },
        this.props.states
      ),
      decoration: this.rowRef.hover
        ? new ui.BoxDecoration({ color: new ui.Color(0xfff5f5f5) })
        : new ui.BoxDecoration({}),
      child: TitleWithCheckbox({
        title: this.template.title,
        value: this.isSelected,
        onChanged: (value) => {
          this._onSelectedCategory(value, this.d3eState);
        },
        className: "x306 hc vc h",
      }),
      onEnter: (event) => {
        this.rowRefOnEnter(event);
      },
      onExit: (event) => {
        this.rowRefOnExit(event);
      },
      className: ui.join(
        this.props.className,
        "D3EAppTemplateSelectionView x76 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
}
function RowRefWithState(props: RowRefWithStateProps) {
  return React.createElement(_RowRefWithState, props);
}

class _D3EAppTemplateSelectionViewState extends ObservableComponent<D3EAppTemplateSelectionViewProps> {
  static defaultProps = {
    template: null,
    onAddTemplateCategory: null,
    onRemoveTemplateCategory: null,
  };
  d3eState: D3EAppTemplateSelectionViewRefs =
    new D3EAppTemplateSelectionViewRefs();
  isSelected: boolean = false;
  public constructor(props: D3EAppTemplateSelectionViewProps) {
    super(props);

    this.initState();
  }
  public get template(): D3EAppTemplateCategory {
    return this.props.template;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("template", this.props.template);

    this.on(["isSelected", "template"], this.rebuild);
  }
  public componentDidUpdate(prevProps: D3EAppTemplateSelectionViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.template !== this.props.template) {
      this.updateObservable(
        "template",
        prevProps.template,
        this.props.template
      );

      this.fire("template", this);
    }
  }
  public setIsSelected(val: boolean): void {
    let isValChanged: boolean = this.isSelected !== val;

    if (!isValChanged) {
      return;
    }

    this.isSelected = val;

    this.fire("isSelected", this);
  }
  public render(): ReactNode {
    return RowRefWithState({
      d3eState: this.d3eState,
      _onSelectedCategory: this.onSelectedCategory,
      isSelected: this.isSelected,
      template: this.template,
      className: this.props.className,
      ...copyBaseUIProps(this.props),
    });
  }
  public onSelectedCategory = (
    value: boolean,
    d3eState: D3EAppTemplateSelectionViewRefs
  ): void => {
    this.setIsSelected(value);

    if (this.isSelected) {
      this.onAddTemplateCategory(this.template);
    } else {
      this.onRemoveTemplateCategory(this.template);
    }
  };
  public get onAddTemplateCategory(): _D3EAppTemplateSelectionViewOnAddTemplateCategory {
    return this.props.onAddTemplateCategory;
  }
  public get onRemoveTemplateCategory(): _D3EAppTemplateSelectionViewOnRemoveTemplateCategory {
    return this.props.onRemoveTemplateCategory;
  }
  public get rowRef() {
    return this.d3eState.rowRef;
  }
}
export default function D3EAppTemplateSelectionView(
  props: D3EAppTemplateSelectionViewProps
) {
  return React.createElement(_D3EAppTemplateSelectionViewState, {
    ..._D3EAppTemplateSelectionViewState.defaultProps,
    ...props,
  });
}
